import { Empty, Flex, Skeleton } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"

import type { RootState } from "../../../../store.ts"
import { selectActiveBranchId } from "../../../Branches/redux/branches.slice.ts"
import {
  PROPOSAL_STATUSES,
  PROPOSAL_STATUSES_OBJ
} from "../../constants/proposal-statuses-keys.ts"
import { proposalsSetFilters } from "../../redux/proposals.slice.ts"
import {
  fetchProposals,
  getProposalFilterOptions
} from "../../redux/proposals.thunks.ts"
import { type ProposalFilters } from "../../redux/proposals.types.ts"
import { ProposalsSidebarHeader } from "./ProposalsSidebarHeader.tsx"
import { ProposalsSidebarList } from "./ProposalsSidebarList.tsx"

function ProposalsSidebar({
  result,
  filters,
  filterOptions,
  fetching,
  viewLeadAllowed,
  activeBranchId,
  getProposals,
  setFilters,
  getFilterOptions
}: ConnectedProps<typeof connector>) {
  const statuses = useMemo(() => {
    const statusMapping: Record<string, string[]> = {
      [PROPOSAL_STATUSES.PENDING_TO_REVIEW]: [
        PROPOSAL_STATUSES.PENDING_TO_REVIEW
      ],
      [PROPOSAL_STATUSES.APPROVED]: [
        PROPOSAL_STATUSES.APPROVED,
        PROPOSAL_STATUSES.SENT_TO_CLIENT
      ],
      [PROPOSAL_STATUSES.CANCELLED]: [PROPOSAL_STATUSES.CANCELLED],
      [PROPOSAL_STATUSES.DECLINED]: [PROPOSAL_STATUSES.DECLINED]
    }

    const out: BaseOptionType[] = []
    for (const s of filterOptions?.status || []) {
      if (!(s?.value in statusMapping)) continue
      out.push({
        ...s,
        value: statusMapping[s.value]!.join(","),
        label: PROPOSAL_STATUSES_OBJ[s.value]?.label ?? s.label
      })
    }
    return out
  }, [filterOptions?.status])

  const handleLoadMoreProposal = () => {
    if (fetching) return
  }

  const handleFilterChange = (
    key: keyof ProposalFilters,
    value: string | null
  ) => {
    const next = { ...filters, [key]: value }
    setFilters({ [key]: value })
    getProposals(next)
  }

  useEffect(() => {
    getProposals(filters)
    getFilterOptions()
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the lists are reloaded only when the active branch changes, with the filters as they are at that moment
  }, [activeBranchId])

  return (
    <Flex
      vertical
      className={"max-w-[410px] border-r w-full shrink-0 border-border"}
    >
      <ProposalsSidebarHeader
        onFilterChange={handleFilterChange}
        statuses={statuses}
        filters={filters}
      />

      {fetching ? (
        <Skeleton.Node className={"size-full"} active />
      ) : (
        <>
          {result?.results?.length ? (
            <ProposalsSidebarList
              hasMore={result?.count > result?.results?.length}
              data={result?.results || []}
              viewLeadAllowed={viewLeadAllowed}
              onLoadMoreProposal={handleLoadMoreProposal}
            />
          ) : (
            <Empty
              className={"my-4"}
              description={
                filters?.q ? "No proposals found" : "No proposals yet"
              }
            />
          )}
        </>
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.proposals.fetchingList,
  filters: state.proposals.filters,
  filterOptions: state.proposals.filterOptions,
  result: state.proposals.result,
  viewLeadAllowed:
    state?.auth?.userInfo?.permissions?.codes?.leads?.leads?.lead_view,
  activeBranchId: selectActiveBranchId(state)
})

const mapDispatchToProps = {
  getProposals: fetchProposals,
  setFilters: proposalsSetFilters,
  getFilterOptions: getProposalFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProposalsSidebar)
